"use client";

import React from "react";
import { Eye, ShoppingBag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
function ProductsCarouselConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="flex gap-6 overflow-hidden">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-[400px] w-full shrink-0 animate-pulse rounded-xl bg-muted/40 md:w-1/2 lg:w-1/3 xl:w-1/4" />)}
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="rounded-xl bg-white py-16 text-center shadow-sm">
        <ShoppingBag className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
        <p className="text-muted-foreground">
          No se encontraron piezas en este momento.
        </p>
      </div>;
  }
  return <Carousel opts={{
    align: "start",
    loop: true
  }} className="w-full">
      <CarouselContent className="-ml-4 md:-ml-6">
        {products.map((product, i) => <CarouselItem key={product.id} data-index={i} className="pl-4 md:basis-1/2 md:pl-6 lg:basis-1/3 xl:basis-1/4">
            <Card className="group flex h-full flex-col overflow-hidden rounded-xl border-none bg-white shadow-sm transition-all duration-300 hover:shadow-md">
              <div className="relative aspect-[4/5] overflow-hidden bg-muted/10">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted/20">
                    <ShoppingBag className="h-12 w-12 text-muted-foreground/20" />
                  </div>}

                <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/5 opacity-0 backdrop-blur-[2px] transition-all duration-300 group-hover:opacity-100">
                  <Button asChild variant="secondary" className="translate-y-4 bg-white/90 text-foreground shadow-lg transition-transform duration-300 group-hover:translate-y-0 hover:bg-white">
                    <Link to={product.link}>
                      <Eye className="mr-2 h-4 w-4" />
                      Vista Rápida
                    </Link>
                  </Button>
                </div>
              </div>

              <CardContent className="flex flex-grow flex-col justify-between px-5 pb-2 pt-6 text-center">
                <Link to={product.link} className="transition-opacity hover:opacity-80">
                  <h3 className="line-clamp-2 font-sans text-lg font-medium leading-tight text-foreground">
                    {product.title}
                  </h3>
                </Link>

                <div className="mt-3 text-lg font-medium tracking-wide text-[#D4AF37]">
                  ${product.price?.toFixed(2) || "0.00"}
                </div>
              </CardContent>

              <CardFooter className="px-5 pb-6 pt-2">
                <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-wider text-xs font-semibold py-6 rounded-none" text="Añadir al Carrito" showQuantity={false} />
              </CardFooter>
            </Card>
          </CarouselItem>)}
      </CarouselContent>

      <div className="hidden lg:block">
        <CarouselPrevious className="-left-12 h-12 w-12 border-none bg-white/80 shadow-md hover:bg-white" />
        <CarouselNext className="-right-12 h-12 w-12 border-none bg-white/80 shadow-md hover:bg-white" />
      </div>
    </Carousel>;
}
export default function ArticulosdePoder() {
  return <section id="articulos-depoder" className="relative bg-[#F0F0F0] py-20 md:py-28">
      <div className="container mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-12 text-center md:mb-16">
          <h2 className="mb-4 font-serif text-3xl text-foreground md:text-4xl lg:text-5xl">
            Piezas con Magia
          </h2>

          <div className="mx-auto mb-6 h-[1px] w-16 bg-[#D4AF37]" />

          <div className="mx-auto max-w-4xl space-y-5 text-muted-foreground">
            <p className="font-sans leading-relaxed">
              Hay objetos que no solo decoran un espacio: lo transforman. En esta
              selección reunimos piezas elegidas por su belleza, su intención y la
              sensación que despiertan al entrar en contacto contigo. Son objetos
              que pueden acompañar rituales personales, momentos de calma, prácticas
              de conexión interior o simplemente recordarte que lo cotidiano también
              puede sentirse sagrado.
            </p>

            <p className="font-sans leading-relaxed">
              Cada pieza ha sido elegida por la experiencia que puede crear en tu
              energía, en tu ambiente y en la manera en que habitas tus espacios.
              Esta es una curaduría para quienes buscan algo más que un producto:
              buscan un objeto con alma, simbolismo y la capacidad de convertir un
              instante común en un pequeño acto de magia.
            </p>
          </div>
        </div>

        <div className="relative px-0 lg:px-12">
          <EcommerceProductsProvider wp_query={{
          per_page: 8
        }}>
            <ProductsCarouselConsumer />
          </EcommerceProductsProvider>
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild size="lg" variant="outline" className="rounded-none border-primary/20 px-10 py-6 text-xs font-semibold uppercase tracking-widest transition-colors hover:bg-primary hover:text-primary-foreground">
            <Link to="/tienda">Ver Todos</Link>
          </Button>
        </div>
      </div>
    </section>;
}